/**
 * PlayPauseAnimatedButton
 *
 * Round button that morphs between play and pause icons using JS-driven
 * coordinate interpolation via requestAnimationFrame. Written directly to the
 * SVG path DOM node — no React state, no re-renders during animation.
 * Cross-browser safe (CSS `d` transitions are unsupported in Safari).
 */

import React, { useRef, useEffect, useCallback } from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ButtonSize } from '@/presentation/components/common/controls/ExpauseButton';
import { PLAY_COORDS, PAUSE_COORDS, coordsToPath } from '@/assets/paths/playPausePaths';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';

function easeInOut(t: number): number {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

interface PlayPauseAnimatedButtonProps {
  isPlaying: boolean;
  size?: ButtonSize;
  onClick?: () => void;
  style?: React.CSSProperties;
}

export const PlayPauseAnimatedButton: React.FC<PlayPauseAnimatedButtonProps> = ({
  isPlaying,
  size = ButtonSize.large,
  onClick,
  style,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();
  const pathRef = useRef<SVGPathElement>(null);
  const rafRef = useRef<number | null>(null);
  // Tracks the coords at the moment a new animation starts, so interruptions
  // animate from the current mid-morph position rather than snapping.
  const currentCoordsRef = useRef<number[]>([...PLAY_COORDS]);

  const sizeToRoundDiameter: Record<ButtonSize, number> = {
    [ButtonSize.small]: responsive.icon.medium * 2,
    [ButtonSize.medium]: responsive.icon.large * 2,
    [ButtonSize.large]: responsive.icon.large * 2.5,
  };
  const sizeToIconSize: Record<ButtonSize, number> = {
    [ButtonSize.small]: responsive.icon.small,
    [ButtonSize.medium]: responsive.icon.medium,
    [ButtonSize.large]: responsive.icon.large,
  };

  const diameter = sizeToRoundDiameter[size];
  const iconSize = sizeToIconSize[size];

  useEffect(() => {
    const target = isPlaying ? PAUSE_COORDS : PLAY_COORDS;
    const from = [...currentCoordsRef.current];
    const startTime = performance.now();

    if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);

    const tick = (now: number) => {
      const elapsed = now - startTime;
      const t = easeInOut(Math.min(elapsed / ANIMATION_CONSTANTS.duration.normal, 1));

      const coords = from.map((v, i) => v + (target[i] - v) * t);
      currentCoordsRef.current = coords;
      pathRef.current?.setAttribute('d', coordsToPath(coords));

      if (t < 1) {
        rafRef.current = requestAnimationFrame(tick);
      } else {
        rafRef.current = null;
      }
    };

    rafRef.current = requestAnimationFrame(tick);
    return () => {
      if (rafRef.current !== null) cancelAnimationFrame(rafRef.current);
    };
  }, [isPlaying]);

  const handleClick = useCallback(() => onClick?.(), [onClick]);

  return (
    <button
      className="inline-flex items-center justify-center focus-visible:outline-none rounded-full hover:opacity-80"
      style={{
        width: diameter,
        height: diameter,
        backgroundColor: `${theme.black}66`,
        padding: 0,
        border: 'none',
        transition: `opacity ${ANIMATION_CONSTANTS.duration.fast}ms ${ANIMATION_CONSTANTS.easing.easeOut}`,
        ...style,
      }}
      onClick={handleClick}
    >
      <svg
        width={iconSize}
        height={iconSize}
        viewBox="16 16 68 68"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          ref={pathRef}
          d={coordsToPath(isPlaying ? PAUSE_COORDS : PLAY_COORDS)}
          fill={theme.white}
          stroke={theme.white}
          strokeWidth={14}
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    </button>
  );
};
